// src/services/aeo/ask.ts
//
// Ask Oura's four backend pieces, shipped in ourteam-backend #6791 (client brief),
// #6795 (the AI step), #6797 (change log) and #6801 (actions), all on main since
// 1 Nov 2026. Each piece sits behind its own switch, explicit `=== "true"`,
// absent means OFF, so the chat keeps its own word matching wherever a route is
// not live yet. Outside the live API every call serves the stand-ins in
// askMock.ts, the way the rest of the AEO service does.
//
// Staff only on the server (a client token gets 403). Every failure here is a
// value, never a throw: the chat shows "did not answer" and keeps going.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import { isOuraLiveApiEnabled } from "./index";
import * as mock from "./askMock";

const AEO_BASE = `${import.meta.env.VITE_TALENT_AI_BE_BASE_URL}/api/v1`;
const useFixtures = !isOuraLiveApiEnabled;
/** Every live call gives up after this, so a stalled connection never leaves an answer slot or Do it hanging. */
const REQUEST_TIMEOUT_MS = 20_000;

/** The AI step answers when the word matching has nothing (POST .../ask). */
export const ASK_AI_ON: boolean = import.meta.env.VITE_ENABLE_OURA_ASK_AI === "true";
/** "What happened since…" reads the change log (GET .../changes). */
export const ASK_CHANGES_ON: boolean = import.meta.env.VITE_ENABLE_OURA_ASK_CHANGES === "true";
/** Chat actions with a preview and a Do it button (GET /oura/actions, POST .../actions/{name}/preview|run). */
export const ASK_ACTIONS_ON: boolean = import.meta.env.VITE_ENABLE_OURA_ASK_ACTIONS === "true";

/** The screens the AI step may cite (the server's own list; anything else is dropped). */
export type AskCite = "overview" | "delivery" | "visibility" | "history" | "client" | "requests" | "documents" | "site" | "search" | "setup";

export interface AskAiTurn {
    role: "user" | "oura";
    text: string;
}

export interface AskAiReply {
    /** 1 to 4 short sentences, plain words. */
    answer: string;
    cites: AskCite[];
    follow_ups: string[];
}

export type AskAiResult = { ok: true; reply: AskAiReply } | { ok: false; code: "unavailable" | "refused"; message: string };

export interface AskChange {
    /** ISO time. */
    at: string;
    event: string;
    /** Plain words: "Scan: named in 32 of 50, up from 28". */
    text: string;
    /** A path into Oura: "/oura/businesses/<id>/delivery". */
    link: string;
}

export interface OuraAction {
    name: string;
    label: string;
    needs: string[];
    optional?: string[];
    reversible: boolean;
    /** How to undo it, in words. */
    undo?: string;
    /** An example of what it does. */
    preview: string;
}

export type ActionPreview = { ok: true; name: string; says: string } | { ok: false; code: string; message: string };
/** `unknown` means the browser got no reply (timeout, dropped connection): the server may still have run it, so the card must not offer a retry. */
export type ActionRun = { ok: true; name: string; says: string; result: unknown; link: string | null } | { ok: false; code: string; message: string; unknown?: true };

/** The server's reason for a refusal: its `message` and the first of `errors`, else the HTTP status. */
function refusalOf(err: unknown): { code: string; message: string } {
    const res = axios.isAxiosError(err) ? err.response : undefined;
    const body = (res?.data ?? {}) as { message?: unknown; errors?: unknown };
    const code = Array.isArray(body.errors) && typeof body.errors[0] === "string" ? body.errors[0] : res ? `http_${res.status}` : "network";
    const message = typeof body.message === "string" && body.message.trim() ? body.message : "Oura did not answer.";
    return { code, message };
}

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    const headers: Record<string, string> = { "Content-Type": "application/json" };
    if (token) headers["Authorization"] = `Bearer ${token}`;
    return headers;
}

const dataOf = <T,>(res: { data?: { data?: T } | T }): T => ((res.data as { data?: T })?.data ?? res.data) as T;

/**
 * One question to Oura's AI step, for one client (its id) or across every
 * client (null). The server reads the brief itself; the browser sends only the
 * question and the last turns. 503 means no model answered within 15 seconds
 * or the month's cap is reached: the chat then falls back to its own "Did you
 * mean".
 */
export async function askOuraAi(businessId: string | null, question: string, history: AskAiTurn[]): Promise<AskAiResult> {
    if (useFixtures) return mock.askOuraAi(businessId, question);
    const url = businessId ? `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/ask` : `${AEO_BASE}/businesses/ask`;
    try {
        const res = await axios.post(url, { question, history: history.slice(-6) }, { headers: authHeaders(), timeout: REQUEST_TIMEOUT_MS });
        const body = dataOf<Partial<AskAiReply> | undefined>(res);
        if (!body || typeof body.answer !== "string" || !body.answer.trim()) return { ok: false, code: "unavailable", message: "Oura's AI gave no answer." };
        return {
            ok: true,
            reply: {
                answer: body.answer,
                cites: Array.isArray(body.cites) ? (body.cites.filter((c): c is AskCite => typeof c === "string") as AskCite[]) : [],
                follow_ups: Array.isArray(body.follow_ups) ? body.follow_ups.filter((f): f is string => typeof f === "string") : [],
            },
        };
    } catch (err) {
        const why = refusalOf(err);
        catchErrorLog(`[aeo] askOuraAi failed for ${businessId ?? "all"}: ${why.code} ${err}`);
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        return { ok: false, code: status === 503 || status === undefined ? "unavailable" : "refused", message: why.message };
    }
}

/**
 * The client's change log, newest first: scans finished, pages live, approvals,
 * Delivery lines done, came back or late, requests, doc shares, the contract,
 * access and the first payment. `since` is YYYY-MM-DD (a Malaysian date); left
 * out, the server gives the last 30 days. Null when it could not be read.
 */
export async function getBusinessChanges(businessId: string, since?: string): Promise<AskChange[] | null> {
    if (useFixtures) return mock.getBusinessChanges(businessId, since);
    try {
        const res = await axios.get(`${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/changes`, {
            headers: authHeaders(),
            params: since ? { since } : undefined,
            timeout: REQUEST_TIMEOUT_MS,
        });
        const body = dataOf<unknown>(res);
        const rows = Array.isArray(body) ? body : Array.isArray((body as { changes?: unknown })?.changes) ? (body as { changes: unknown[] }).changes : null;
        if (!rows) return null;
        return rows.filter((r): r is AskChange => !!r && typeof (r as AskChange).at === "string" && typeof (r as AskChange).text === "string");
    } catch (err) {
        catchErrorLog(`[aeo] getBusinessChanges failed for ${businessId}: ${err}`);
        return null;
    }
}

let actionsCache: Promise<OuraAction[] | null> | null = null;

/** The actions the server lets a chat run, read once per tab. Null when the list could not be read. */
export function listOuraActions(): Promise<OuraAction[] | null> {
    if (useFixtures) return Promise.resolve(mock.listOuraActions());
    if (!actionsCache) {
        actionsCache = axios
            .get(`${AEO_BASE}/oura/actions`, { headers: authHeaders(), timeout: REQUEST_TIMEOUT_MS })
            .then((res) => {
                const body = dataOf<unknown>(res);
                return Array.isArray(body) ? (body.filter((a) => !!a && typeof (a as OuraAction).name === "string") as OuraAction[]) : null;
            })
            .catch((err) => {
                catchErrorLog(`[aeo] listOuraActions failed: ${err}`);
                actionsCache = null;
                return null;
            });
    }
    return actionsCache;
}

/** What the action would change, in words. Reads only; nothing it touched is kept. */
export async function previewOuraAction(businessId: string, name: string, params: Record<string, unknown>): Promise<ActionPreview> {
    if (useFixtures) return mock.previewOuraAction(businessId, name, params);
    try {
        const res = await axios.post(`${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/actions/${encodeURIComponent(name)}/preview`, { params }, { headers: authHeaders(), timeout: REQUEST_TIMEOUT_MS });
        const body = dataOf<{ name?: string; says?: string } | undefined>(res);
        if (!body || typeof body.says !== "string") return { ok: false, code: "bad_reply", message: "Oura did not say what it would do." };
        return { ok: true, name: body.name ?? name, says: body.says };
    } catch (err) {
        const why = refusalOf(err);
        catchErrorLog(`[aeo] previewOuraAction ${name} failed for ${businessId}: ${why.code} ${err}`);
        return { ok: false, ...why };
    }
}

const UNKNOWN_RUN: ActionRun = { ok: false, code: "unknown", message: "Oura did not confirm whether it ran.", unknown: true };

/**
 * Does it, and returns the link to where it shows. Only after a person pressed
 * Do it. `idempotencyKey` is one key per card, sent as `Idempotency-Key`, so a
 * server that replays the first result for a repeated key never runs the
 * action twice (the route does not read it yet; until it does, no reply at all
 * comes back as `unknown` and the card offers no retry).
 */
export async function runOuraAction(businessId: string, name: string, params: Record<string, unknown>, idempotencyKey?: string): Promise<ActionRun> {
    if (useFixtures) return mock.runOuraAction(businessId, name, params);
    try {
        const headers = idempotencyKey ? { ...authHeaders(), "Idempotency-Key": idempotencyKey } : authHeaders();
        const res = await axios.post(`${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/actions/${encodeURIComponent(name)}/run`, { params }, { headers, timeout: REQUEST_TIMEOUT_MS });
        const body = dataOf<{ name?: string; says?: string; result?: unknown; link?: unknown } | undefined>(res);
        // A 2xx without the action's words: it probably ran, so no retry.
        if (!body || typeof body.says !== "string") return UNKNOWN_RUN;
        return { ok: true, name: body.name ?? name, says: body.says, result: body.result ?? null, link: typeof body.link === "string" ? body.link : null };
    } catch (err) {
        const why = refusalOf(err);
        catchErrorLog(`[aeo] runOuraAction ${name} failed for ${businessId}: ${why.code} ${err}`);
        // No reply (timeout, dropped connection), 408 or a 5xx: the server may have run it anyway. A 4xx is a refusal it sent on purpose.
        const status = axios.isAxiosError(err) ? err.response?.status : undefined;
        if (status === undefined || status === 408 || status >= 500) return UNKNOWN_RUN;
        return { ok: false, ...why };
    }
}
